import Swiper, { Navigation, Pagination, Autoplay } from 'swiper';
import {swiperObserver} from '../../utils/swiper.utils.js';
import {buildStorySlideGroups, shouldUseStoryRowLayout} from './stories-layout.js';

const activeFilterPresetClasses = ['cl-dark', 'bg-white', 'bcl-white'];
const inactiveFilterPresetClasses = ['cl-white', 'bg-black', 'bcl-black'];
const filterPresetClasses = [...activeFilterPresetClasses, ...inactiveFilterPresetClasses];

const filtersOverflow = (swiper) => {
  if (!swiper?.el || !swiper.slides?.length) {
    return false;
  }

  const lastSlide = swiper.slides[swiper.slides.length - 1];
  const slidesWidth = lastSlide.offsetLeft + lastSlide.offsetWidth;

  return slidesWidth > swiper.el.offsetWidth;
};

const scrollFilterIntoView = (swiper, index) => {
  if (!swiper || swiper.destroyed || !filtersOverflow(swiper)) {
    return;
  }

  const slide = swiper.slides[index];

  if (!slide) {
    return;
  }

  const containerWidth = swiper.el.offsetWidth;
  const visibleLeft = -swiper.translate;
  const visibleRight = visibleLeft + containerWidth;
  const slideLeft = slide.offsetLeft;
  const slideRight = slideLeft + slide.offsetWidth;

  if (slideLeft >= visibleLeft && slideRight <= visibleRight) {
    return;
  }

  const inset = 16;
  const targetTranslate = slideLeft < visibleLeft
    ? -slideLeft + inset
    : -(slideRight - containerWidth + inset);
  const translate = Math.min(swiper.maxTranslate(), Math.max(swiper.minTranslate(), targetTranslate));

  swiper.translateTo(translate, 300);
};

const setActiveFilter = (filterButtons, activeButton) => {
  filterButtons.forEach((button) => {
    const isActive = button === activeButton;

    button.classList.toggle('is-active', isActive);
    button.classList.remove(...filterPresetClasses);
    button.classList.add(...(isActive ? activeFilterPresetClasses : inactiveFilterPresetClasses));
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
};

const initStoriesSwiper = (scope = document) => {
  scope.querySelectorAll('.stories-swiper-wr').forEach((wrapperEl) => {
    if (wrapperEl.dataset.storiesInitialized) {
      return;
    }

    const swiperEl = wrapperEl.querySelector('.swiper');
    const slidesWrapper = swiperEl?.querySelector('.swiper-wrapper');

    if (!swiperEl || !slidesWrapper) {
      return;
    }

    wrapperEl.dataset.storiesInitialized = 'true';

    const sectionEl = wrapperEl.closest('.stories-sec');
    const filterButtons = sectionEl?.querySelectorAll('[data-story-industry]') || [];
    const filtersSwiperEl = sectionEl?.querySelector('.testimonials-sec__filters-swiper .swiper');
    const nextEl = wrapperEl.querySelector('.swiper-button-next');
    const prevEl = wrapperEl.querySelector('.swiper-button-prev');
    const paginationEl = wrapperEl.querySelector('.swiper-pagination');
    const originalHTML = slidesWrapper.innerHTML;
    let activeIndustry = 'all';
    let swiper;
    let observer;
    const filtersSwiper = filtersSwiperEl
      ? filtersSwiperEl.swiper || new Swiper(filtersSwiperEl, {
        observer: true,
        observeParents: true,
        spaceBetween: 10,
        slidesPerView: 'auto',
        threshold: 10,
        swipeToSlide: true,
        watchOverflow: true,
        breakpoints: {
          641: {
            spaceBetween: 12,
          },
          1023: {
            spaceBetween: 14,
          },
        },
      })
      : null;

    const markSingleSlides = () => {
      slidesWrapper.querySelectorAll('.swiper-slide').forEach((slide) => {
        const count = slide.querySelectorAll('.success-stories-card').length;
        slide.classList.toggle('swiper-slide--single', count === 1);
      });
    };

    const splitForMobile = () => {
      const slides = Array.from(slidesWrapper.querySelectorAll('.swiper-slide'));

      slides.forEach((slide) => {
        const cards = Array.from(slide.querySelectorAll('.success-stories-card'));

        cards.slice(1).reverse().forEach((card) => {
          const nextSlide = document.createElement('div');
          nextSlide.className = 'swiper-slide';
          nextSlide.appendChild(card);
          slide.insertAdjacentElement('afterend', nextSlide);
        });
      });

      markSingleSlides();
    };

    const getTemplateCards = () => {
      const template = document.createElement('template');
      template.innerHTML = originalHTML;

      return Array.from(template.content.querySelectorAll('.swiper-slide')).flatMap((slide, sourceSlideIndex) => (
        Array.from(slide.querySelectorAll('.success-stories-card')).map((card) => ({
          element: card,
          isBig: card.dataset.storyBig === '1',
          industries: (card.dataset.storyIndustries || '').split('|'),
          sourceSlideIndex,
        }))
      ));
    };

    const renderFilteredSlides = () => {
      const cards = getTemplateCards().filter((card) => card.industries.includes(activeIndustry));
      const isMobile = window.innerWidth < 768;
      const preservedGroups = buildStorySlideGroups(cards, isMobile);
      const useRowLayout = shouldUseStoryRowLayout(preservedGroups, isMobile);
      const groups = useRowLayout ? cards.map((card) => [card]) : preservedGroups;
      const fragment = document.createDocumentFragment();

      sectionEl?.classList.toggle('stories-sec--row-layout', useRowLayout);

      groups.forEach((group) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';

        group.forEach((card) => {
          slide.appendChild(card.element);
        });

        fragment.appendChild(slide);
      });

      slidesWrapper.replaceChildren(fragment);
      markSingleSlides();
    };

    const renderSlides = () => {
      if (activeIndustry === 'all') {
        sectionEl?.classList.remove('stories-sec--row-layout');
        slidesWrapper.innerHTML = originalHTML;

        if (window.innerWidth < 768) {
          splitForMobile();
        } else {
          markSingleSlides();
        }

        return;
      }

      renderFilteredSlides();
    };

    const createSwiper = () => {
      const useRowLayout = sectionEl?.classList.contains('stories-sec--row-layout');

      return new Swiper(swiperEl, {
        modules: [Navigation, Pagination, Autoplay],
        observer: true,
        observeParents: true,
        spaceBetween: useRowLayout ? 12 : 16,
        slidesPerView: 1,
        threshold: 10,
        navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
        pagination: paginationEl ? { el: paginationEl, clickable: true } : undefined,
        breakpoints: useRowLayout ? {
          500: {
            spaceBetween: 20,
            slidesPerView: 1,
          },
          641: {
            spaceBetween: 20,
            slidesPerView: 1.3,
          },
          961: {
            spaceBetween: 24,
            slidesPerView: 1.5,
          },
          1100: {
            spaceBetween: 28,
            slidesPerView: 2.05,
          },
        } : {
          320: {
            spaceBetween: 20,
            slidesPerView: 1,
          },
          767: {
            spaceBetween: 20,
            slidesPerView: 1,
          },
          961: {
            spaceBetween: 24,
            slidesPerView: 1,
          },
          1100: {
            spaceBetween: 30,
            slidesPerView: 1,
          },
        },
        autoplay: slidesWrapper.children.length > 1 ? {
          delay: 4000,
          disableOnInteraction: false,
          pauseOnMouseEnter: window.innerWidth >= 1033,
        } : false,
      });
    };

    const refresh = () => {
      observer?.disconnect();
      swiper?.destroy(true, true);
      renderSlides();
      window.initBtnRoll?.();
      window.initSigilFigureAnimation?.();
      swiper = createSwiper();
      observer = swiperObserver(swiper);
    };

    if (filterButtons.length) {
      const activeFilter = sectionEl?.querySelector('[data-story-industry].is-active') || filterButtons[0];

      setActiveFilter(filterButtons, activeFilter);
    }

    filterButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        activeIndustry = button.dataset.storyIndustry || 'all';
        setActiveFilter(filterButtons, button);
        scrollFilterIntoView(filtersSwiper, index);
        refresh();
      });
    });

    let isMobile = window.innerWidth < 768;
    window.addEventListener('resize', () => {
      const nextIsMobile = window.innerWidth < 768;
      if (nextIsMobile !== isMobile) {
        isMobile = nextIsMobile;
        refresh();
      }
    }, { passive: true });

    refresh();
  });
};

initStoriesSwiper();
window.initStoriesSwiper = initStoriesSwiper;
